import React from 'react';
import styled from 'styled-components';
import { FlexBox, StackPanel, TagValue, Typography, useNotificationEvents } from '@agilepoint/wat-react-ux';
import { Localize, useTranslate } from '@agilepoint/wat-react';
import { ISaveTextDataResponse } from 'modules/shared/queries';
import { StyledEmailInputControl } from 'modules/shared/styled-components';
import { StyledStackPanel } from 'common';
import { TabFooterButtons } from './tab-footer-buttons';
import { useDefaultSettingsContext } from './app-settings-layout';

interface INotificationListProps {
    mutationLoading: boolean;
    notificationList: string[]
    onSaveNotificationListDetails: (args: string) => Promise<ISaveTextDataResponse>;
}

const NotificationListHeader = styled(Typography.H2)`
    max-width: 545px;
    text-align: center;  
`;

export const NotificationList = React.memo((props: INotificationListProps) => {
    const { notificationList, mutationLoading, onSaveNotificationListDetails } = props;
    const [tagInputItems, setTagInputItems] = React.useState<TagValue[]>(notificationList);
    const { showNotifications } = useNotificationEvents();
    const { defaultSettingsConfig, setDefaultSettingsConfig } = useDefaultSettingsContext();
    const translate = useTranslate();

    const onTagInputChange = React.useCallback((values: TagValue[]) => {
        setTagInputItems(values);
    }, []);

    const onSaveClick = React.useCallback(() => {
        onSaveNotificationListDetails(tagInputItems.join(','))
            .then(() => {
                showNotifications({ message: translate('modules.defaultAppSettings.notificationList.successfulListInfo'), type: 'success' });
                setDefaultSettingsConfig({
                    doesNotIncludesAppListConfig: defaultSettingsConfig.doesNotIncludesAppListConfig,
                    doesNotIncludesNotificationList: !tagInputItems.length
                });
            })
            .catch(() => showNotifications({ message: translate('modules.defaultAppSettings.notificationList.failureListText'), type: 'error' }));
    }, [onSaveNotificationListDetails, tagInputItems, showNotifications, translate, setDefaultSettingsConfig,
        defaultSettingsConfig.doesNotIncludesAppListConfig]);

    return (
        <FlexBox justifyContent="space-between" flexDirection='column' height="calc(100% - 16px)">
            <StyledStackPanel margin='30px 0px 0px 0px' orientation='vertical' gap='30' width='100%' alignment="center" justifyContent="center">
                <StackPanel gap="10" orientation='vertical' alignment="center" width='100%'>
                    <NotificationListHeader title={translate('modules.defaultAppSettings.notificationList.notificationListHeader')}><Localize translate='modules.defaultAppSettings.notificationList.notificationListHeader' /></NotificationListHeader>
                    <Typography.Medium title={translate('modules.createNewApp.steps.buildApp.buildAppSubTitle')}><Localize translate='modules.createNewApp.steps.buildApp.buildAppSubTitle' /></Typography.Medium>
                </StackPanel>
                <StyledEmailInputControl
                    enableClearList
                    tagInputItems={tagInputItems}
                    onChange={onTagInputChange} />
            </StyledStackPanel>
            <TabFooterButtons onSubmitClickHandler={onSaveClick} mutationLoading={mutationLoading} />
        </FlexBox>
    );
});
